import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/auth';
import { api } from '../lib/api';
import { DEPENDENT_COLORS } from '../components/DependentBadge';
import type { Dependent, JournalEntry } from '@kinapp/shared';

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatDateGroup(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

function avatarInitial(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function JournalPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [familyId, setFamilyId] = useState('');
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  // Compose state
  const [content, setContent] = useState('');
  const [selectedDepId, setSelectedDepId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [error, setError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const families = await api.getFamilies();
        if (!families?.length) { navigate('/onboarding'); return; }
        const fid = families[0].id;
        setFamilyId(fid);

        const [deps, journalEntries, members] = await Promise.all([
          api.getDependents(fid, true),
          api.getJournalEntries(fid, { limit: 50 }),
          api.getFamilyMembers(fid),
        ]);

        setDependents(deps as Dependent[]);
        setEntries(journalEntries as JournalEntry[]);

        const me = (members as any[]).find((m: any) => m.user?.id === user?.id);
        if (me?.role === 'OWNER') setIsOwner(true);
      } catch {
        setError('Unable to load journal. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, user?.id]);

  const handleSubmit = async () => {
    if (!content.trim() || !familyId) return;
    setSaving(true);
    setComposeError('');
    try {
      const entry = await api.createJournalEntry(familyId, {
        content: content.trim(),
        dependentId: selectedDepId ?? undefined,
      }) as JournalEntry;
      setEntries((prev) => [entry, ...prev]);
      setContent('');
      setSelectedDepId(null);
    } catch (err: any) {
      setComposeError(err?.message ?? 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    try {
      await api.deleteJournalEntry(familyId, entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch {
      // ignore
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Group entries by calendar date
  const grouped: Array<{ dateLabel: string; entries: JournalEntry[] }> = [];
  for (const entry of entries) {
    const label = formatDateGroup(entry.createdAt);
    const last = grouped[grouped.length - 1];
    if (last && last.dateLabel === label) {
      last.entries.push(entry);
    } else {
      grouped.push({ dateLabel: label, entries: [entry] });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col gap-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Care Journal</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          A shared record of observations, moments, and notes for the whole family.
        </p>
      </div>

      {/* Compose box */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What happened today? Any observations to share with the family…"
          rows={3}
          className="w-full resize-none text-sm text-gray-800 placeholder-gray-400 border-0 outline-none focus:ring-0 p-0"
        />

        {/* Dependent tag row */}
        {dependents.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <span className="text-xs text-gray-400 self-center mr-0.5">About</span>
            {dependents.map((dep, i) => {
              const colors = DEPENDENT_COLORS[i % DEPENDENT_COLORS.length];
              const selected = selectedDepId === dep.id;
              return (
                <button
                  key={dep.id}
                  onClick={() => setSelectedDepId(selected ? null : dep.id)}
                  className={`inline-flex items-center text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    selected
                      ? `${colors?.bg ?? 'bg-indigo-100'} ${colors?.text ?? 'text-indigo-700'} border-transparent font-medium`
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {dep.name}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between pt-1 border-t border-gray-100">
          {composeError ? (
            <p className="text-xs text-red-500">{composeError}</p>
          ) : (
            <p className="text-xs text-gray-400">⌘ Return to save</p>
          )}
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || saving}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>

      {/* Feed */}
      {entries.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          <p className="text-3xl mb-2">📓</p>
          <p>No journal entries yet.</p>
          <p className="mt-1 text-xs">Be the first to add an observation above.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {grouped.map(({ dateLabel, entries: dayEntries }) => (
            <div key={dateLabel}>
              {/* Date divider */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs font-medium text-gray-400 shrink-0">{dateLabel}</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              <div className="flex flex-col gap-3">
                {dayEntries.map((entry) => {
                  const depIndex = dependents.findIndex((d) => d.id === entry.dependentId);
                  const depColors = depIndex >= 0 ? DEPENDENT_COLORS[depIndex % DEPENDENT_COLORS.length] : null;
                  const canDelete = entry.authorId === user?.id || isOwner;

                  return (
                    <div
                      key={entry.id}
                      className="bg-white rounded-xl border border-gray-100 p-4 flex gap-3"
                    >
                      {/* Avatar */}
                      <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center shrink-0">
                        {avatarInitial(entry.author.name)}
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{entry.author.name}</span>
                          {entry.dependent && depColors && (
                            <span
                              className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full ${depColors.bg} ${depColors.text}`}
                            >
                              {entry.dependent.name}
                            </span>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">
                            {formatRelativeTime(entry.createdAt)}
                          </span>
                        </div>
                        <p className="mt-1.5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {entry.content}
                        </p>
                      </div>

                      {/* Delete */}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(entry.id)}
                          title="Delete entry"
                          className="text-gray-300 hover:text-red-400 transition-colors shrink-0 mt-0.5"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
